// 위젯 추가 카탈로그 — 팔레트 select("+위젯")와 배치 행 우클릭 메뉴('위젯 추가 ›')의 **공통 정본**.
//
// ⚠️ 두 진입점은 반드시 같은 목록을 만들어야 한다 — 한쪽에만 위젯 종류를 추가하면 진입점에 따라
// 만들 수 있는 위젯이 달라진다(2026-07-22 '모달 버튼'이 우클릭에만 빠진 실사고). 목록을 두 벌
// 유지하지 않도록 값·라벨·게이트를 이 파일 한 배열(CORE_ADD_WIDGET_ENTRIES)에서 파생시킨다.
// 새 위젯 종류를 추가할 때는 (1) 여기 entry (2) AddWidgetCallbacks 콜백
// (3) dispatchAddWidgetValue 분기 — 셋을 함께 갱신할 것(단위 테스트가 배선 누락을 잡는다).

import { REGISTERED_FORM_WIDGETS_META } from '@safetyhub/shared';

/** 위젯 추가 디스패치 콜백 묶음 — 팔레트 select(루트·분기 슬롯)와 우클릭 메뉴가 값 규약을 공유한다. */
export interface AddWidgetCallbacks {
  onAddGroup: () => void;
  onAddDesign: (kind: 'heading' | 'separator' | 'spacer') => void;
  onAddRegistered: (widgetKey: string) => void;
  onAddFormTable: () => void;
  /** 독립 입력 위젯(treeOwned) 추가 — 카탈로그 자동 채움은 속성에서 지정. */
  onAddInput: () => void;
  /** 커스텀 모달 버튼 위젯 추가. */
  onAddModalTrigger: () => void;
  /** 반복 위젯 추가 — 빈 반복(over 미지정), 대상은 속성 패널에서 지정. */
  onAddRepeat: () => void;
  /** 분기 위젯 추가 — 슬롯 2개(빈 else), 슬롯 조건은 속성 패널에서 지정. */
  onAddBranch: () => void;
  /** 프리셋 삽입(클론+presetRef 스탬프 — 우클릭 메뉴 '프리셋'과 동일 경로). */
  onInsertPreset: (presetId: string) => void;
}

/** 추가 목록 1항목 — select의 option / 우클릭 메뉴의 action이 같은 값·라벨로 파생된다. */
export interface AddWidgetEntry {
  value: string;
  label: string;
}

/**
 * core(기본) 위젯 카탈로그 — 값·라벨·순서·게이트의 정본.
 * gate가 있는 항목은 삽입 대상 부모에 따라 숨겨진다(addWidgetGates 참조).
 */
export const CORE_ADD_WIDGET_ENTRIES: readonly (AddWidgetEntry & {
  gate?: 'modalTrigger' | 'repeat';
})[] = [
  { value: 'core:group', label: '섹션' },
  // heading은 자유 텍스트 요소 — UI 명칭 '텍스트'(2026-07-12 사용자 정정, 코드 kind는 heading 유지).
  { value: 'core:heading', label: '텍스트' },
  { value: 'core:separator', label: '구분선' },
  // 여백(spacer) — 로직·렌더는 있었으나 팔레트 미노출이었음(2026-07-13 사용자 발견).
  { value: 'core:spacer', label: '여백' },
  { value: 'core:formTable', label: '양식 표' },
  { value: 'core:input', label: '입력' },
  { value: 'core:modalTrigger', label: '모달 버튼', gate: 'modalTrigger' },
  // 반복 = 범용 구조 컨테이너(빈 반복 추가 → 속성 패널에서 대상 데이터소스 지정, 지정 시 뼈대
  // 자동 생성). 데이터소스 이름은 팔레트에 나열 안 함(고유 데이터). 미배치 풀 배치(강제 단일화)와
  // 공존 — 풀=특정 소스 즉시 배치, 팔레트=빈 반복 후 지정. 2026-07-20 사용자 요청.
  { value: 'core:repeat', label: '반복', gate: 'repeat' },
  // 분기 = 조건에 따라 슬롯 중 하나만 렌더하는 투명 컨테이너. 게이트 없음(모든 컨테이너 안 허용,
  // 중첩 branch도 본문처럼 허용). 슬롯 조건은 속성 패널에서 지정. 2026-07-20 P3.
  { value: 'core:branch', label: '분기' },
];

/**
 * core 위젯 목록 — 게이트 적용본.
 * @param opts.allowModalTrigger 모달 버튼 노출 여부 — modalTrigger 안(모달 children 추가)에선 중첩 금지로 false. 미지정=true.
 * @param opts.allowRepeat 반복 노출 여부 — 모달 안에선 false(모달 스코프는 행 스코프 아님).
 *   반복 안 중첩 반복은 허용(P4 정식 저작 어휘) — 게이트 정본은 addWidgetGates. 미지정=true.
 */
export function coreAddWidgetEntries(opts?: {
  allowModalTrigger?: boolean;
  allowRepeat?: boolean;
}): AddWidgetEntry[] {
  const allowModalTrigger = opts?.allowModalTrigger ?? true;
  const allowRepeat = opts?.allowRepeat ?? true;
  return CORE_ADD_WIDGET_ENTRIES.filter((e) => {
    if (e.gate === 'modalTrigger') return allowModalTrigger;
    if (e.gate === 'repeat') return allowRepeat;
    return true;
  }).map((e) => ({ value: e.value, label: e.label }));
}

/**
 * 등록형(특화) 위젯 목록 — 범용 카탈로그.
 * rowScopeOnly(위험도·개선계획)는 본문 팔레트 제외(행 그리드 뼈대 전용, P4b).
 */
export function registeredAddWidgetEntries(): AddWidgetEntry[] {
  return REGISTERED_FORM_WIDGETS_META.filter((m) => !m.rowScopeOnly).map((m) => ({
    value: `reg:${m.key}`,
    label: m.label,
  }));
}

/**
 * 위젯 추가 값(`core:*`/`reg:*`/`preset:*`) → 콜백 디스패치.
 * @returns 처리했으면 true, 미인식 값이면 false(카탈로그 항목에 배선이 있는지 테스트가 검증).
 */
export function dispatchAddWidgetValue(value: string, cb: AddWidgetCallbacks): boolean {
  if (value === 'core:group') cb.onAddGroup();
  else if (value === 'core:heading') cb.onAddDesign('heading');
  else if (value === 'core:separator') cb.onAddDesign('separator');
  else if (value === 'core:spacer') cb.onAddDesign('spacer');
  else if (value === 'core:formTable') cb.onAddFormTable();
  else if (value === 'core:input') cb.onAddInput();
  else if (value === 'core:modalTrigger') cb.onAddModalTrigger();
  else if (value === 'core:repeat') cb.onAddRepeat();
  else if (value === 'core:branch') cb.onAddBranch();
  else if (value.startsWith('reg:')) cb.onAddRegistered(value.slice(4));
  else if (value.startsWith('preset:')) cb.onInsertPreset(value.slice(7));
  else return false;
  return true;
}
